import { useId } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Eraser, Pencil, X } from 'lucide-react'

import { FieldStateBadge } from '@/components/fields/fieldState'
import { referenceCategoryFor } from '@/components/deal/dossier/editableFields'
import { formatFieldValue } from '@/components/deal/dossier/format'
import { referenceDataService } from '@/services/referenceData'
import type {
  ConfirmationQuestion,
  ConfirmationRevision,
  ConfirmationRow,
} from '@/services/dealConfirmation'
import { editDraftOf, editNeedsReason, statusDetail, statusMeta, valueText } from './ConfirmationViewModel'

/**
 * One field of the confirmation view: Field | Value | Source | Status | actions.
 *
 * The row draws what the server said and offers only what the server said a
 * person may do: a derived output offers no Confirm, a pipeline-owned field no
 * Edit, a missing value no Confirm at all — its box is the action. Nothing here
 * writes; every button hands the controller a decision about THIS field.
 */

export const ROW_GRID =
  'grid grid-cols-[minmax(0,1.05fr)_minmax(0,1.5fr)_minmax(0,1.4fr)_minmax(0,1fr)_104px] items-start gap-x-3'

export interface RowController {
  /** Whether this account may change anything at all. */
  mayEdit: boolean
  /** The field a write is in flight for, or 'bulk'. */
  busy: string | null
  draft: (field: string) => string | undefined
  reason: (field: string) => string
  error: (field: string) => string | undefined
  clearing: string | null
  setDraft: (field: string, value: string | undefined) => void
  setReason: (field: string, value: string) => void
  confirm: (row: ConfirmationRow) => void
  save: (row: ConfirmationRow) => void
  askClear: (field: string | null) => void
  clear: (row: ConfirmationRow) => void
  pick: (question: ConfirmationQuestion, optionIndex: number) => void
  applyRevision: (revision: ConfirmationRevision, apply: boolean) => void
}

function ValueEditor({ row, controller }: { row: ConfirmationRow; controller: RowController }) {
  const listId = useId()
  const category = referenceCategoryFor(row.field)
  const { data: vocabulary = [] } = useQuery({
    queryKey: ['reference-values', category],
    queryFn: () => referenceDataService.listValues(category as string),
    enabled: category !== null,
    staleTime: 5 * 60_000,
    // A desk user may not hold `reference_data:view`; the box stays plain text.
    retry: false,
  })
  const draft = controller.draft(row.field) ?? ''
  const busy = controller.busy !== null
  const label = `${row.label} value`
  const common = {
    'aria-label': label,
    value: draft,
    disabled: busy,
    onChange: (e: { target: { value: string } }) => controller.setDraft(row.field, e.target.value),
    className:
      'w-full rounded border border-stone-700 bg-black/40 px-2 py-1 text-[12px] text-stone-100 outline-none focus:border-amber-300/50 disabled:opacity-60',
  }
  const choices = row.kind === 'select' && row.options.length > 0 && category === null ? row.options : null

  return (
    <div className="space-y-1">
      {choices ? (
        <select {...common}>
          <option value="">—</option>
          {draft && !choices.includes(draft) ? <option value={draft}>{draft}</option> : null}
          {choices.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <>
          <input
            {...common}
            type={row.kind === 'date' ? 'date' : 'text'}
            placeholder={row.status.startsWith('missing') ? 'Enter a value' : undefined}
            list={vocabulary.length > 0 ? listId : undefined}
            autoComplete={vocabulary.length > 0 ? 'off' : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                controller.save(row)
              }
              if (e.key === 'Escape' && !row.status.startsWith('missing')) {
                e.stopPropagation()
                controller.setDraft(row.field, undefined)
              }
            }}
          />
          {vocabulary.length > 0 ? (
            <datalist id={listId}>
              {vocabulary.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          ) : null}
        </>
      )}
      {editNeedsReason(row) && draft.trim() ? (
        <input
          aria-label={`${row.label} reason`}
          value={controller.reason(row.field)}
          disabled={busy}
          onChange={(e) => controller.setReason(row.field, e.target.value)}
          placeholder="Reason — one line, required for a figure"
          className="w-full rounded border border-stone-800 bg-black/30 px-2 py-1 text-[11px] text-stone-300 outline-none placeholder:text-stone-600 focus:border-amber-300/40"
        />
      ) : null}
      {draft.trim() || !row.status.startsWith('missing') ? (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => controller.save(row)}
            disabled={busy || !draft.trim()}
            aria-label={`Save ${row.label}`}
            className="inline-flex items-center gap-1 rounded bg-amber-300 px-2 py-0.5 text-[10px] font-medium text-black disabled:opacity-50"
          >
            <Check className="h-3 w-3" /> Save
          </button>
          {!row.status.startsWith('missing') ? (
            <button
              type="button"
              onClick={() => controller.setDraft(row.field, undefined)}
              disabled={busy}
              aria-label={`Cancel editing ${row.label}`}
              className="inline-flex items-center gap-1 rounded border border-stone-700 px-2 py-0.5 text-[10px] text-stone-300 disabled:opacity-50"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function SourceCell({ row }: { row: ConfirmationRow }) {
  // A flag asks "is this right?" — a person confirming the value has answered it.
  const flags = row.status === 'confirmed' ? [] : row.questions.filter((q) => !q.conflict)
  const summary = row.source.summary
  const present = row.status !== 'missing_required' && row.status !== 'missing_optional' && row.status !== 'not_applicable'
  return (
    <div className="min-w-0 space-y-0.5">
      {present && row.state && row.status !== 'confirmed' ? (
        <FieldStateBadge state={row.state} title={summary || undefined} />
      ) : null}
      {present && summary ? (
        <div className="line-clamp-2 break-words text-[10px] leading-snug text-stone-400" title={summary}>
          {summary}
        </div>
      ) : null}
      {present && row.source.attribution && row.source.attribution !== 'unattributed' ? (
        <div className="text-[9px] uppercase tracking-wider text-stone-600" title={row.source.attributionWhy ?? undefined}>
          {row.source.attribution}
        </div>
      ) : null}
      {flags.map((q) => (
        <div key={q.id} className="text-[10px] leading-snug text-amber-300/90" title={q.question ?? undefined}>
          Flagged — {q.question}
        </div>
      ))}
    </div>
  )
}

function ConflictPanel({ row, controller }: { row: ConfirmationRow; controller: RowController }) {
  const conflicts = row.questions.filter((q) => q.conflict)
  if (conflicts.length === 0 && row.revisions.length === 0) return null
  return (
    <div className="col-span-5 mt-1 space-y-2 rounded border border-red-400/20 bg-red-400/[0.04] px-3 py-2">
      {conflicts.map((q) => (
        <div key={q.id} className="space-y-1">
          <div className="text-[11px] text-red-200">{q.question ?? 'The sources disagree — choose the right value.'}</div>
          <div className="flex flex-wrap gap-1.5">
            {q.options.length === 0 ? (
              <span className="text-[10px] text-stone-500">No rival values were recorded.</span>
            ) : (
              q.options.map((option, index) => (
                <button
                  key={`${q.id}:${index}`}
                  type="button"
                  disabled={!controller.mayEdit || controller.busy !== null}
                  onClick={() => controller.pick(q, index)}
                  aria-label={`Use ${formatFieldValue(option.value, row.kind, row.field) || String(option.value)} for ${row.label}`}
                  className="max-w-full rounded border border-stone-600 bg-black/40 px-2 py-1 text-left text-[11px] text-stone-100 hover:border-amber-300/50 disabled:opacity-50"
                >
                  <span className="font-mono">
                    {formatFieldValue(option.value, row.kind, row.field) || String(option.value)}
                  </span>
                  {option.source ? (
                    <span className="mt-0.5 block truncate text-[9px] text-stone-500">{option.source}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ))}
      {row.revisions.map((rev) => (
        <div key={`${rev.ingestionId}:${rev.field}`} className="space-y-1">
          <div className="text-[11px] text-amber-200">
            A later email{rev.from ? ` from ${rev.from}` : ''} proposes{' '}
            <span className="font-mono">{valueOrNone(rev.incoming, row)}</span>
            {rev.current === null || rev.current === undefined || rev.current === '' ? (
              <> — the deal holds no value for it until you decide.</>
            ) : (
              <>
                {' '}— the deal keeps <span className="font-mono">{valueOrNone(rev.current, row)}</span> until you
                decide.
              </>
            )}
          </div>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={!controller.mayEdit || controller.busy !== null}
              onClick={() => controller.applyRevision(rev, true)}
              aria-label={`Apply the later email's value to ${row.label}`}
              className="rounded border border-amber-300/40 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-300/10 disabled:opacity-50"
            >
              Apply the later value
            </button>
            <button
              type="button"
              disabled={!controller.mayEdit || controller.busy !== null}
              onClick={() => controller.applyRevision(rev, false)}
              aria-label={`Keep the current value of ${row.label}`}
              className="rounded border border-stone-600 px-2 py-0.5 text-[10px] text-stone-300 hover:bg-white/5 disabled:opacity-50"
            >
              Keep the current value
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

/** A proposed or held value as a reader expects it ("none" for nothing). */
function valueOrNone(value: unknown, row: ConfirmationRow): string {
  if (value === null || value === undefined || value === '') return 'none'
  return formatFieldValue(value, row.kind, row.field) || String(value)
}

export function ConfirmationViewRow({ row, controller }: { row: ConfirmationRow; controller: RowController }) {
  const meta = statusMeta(row.status)
  const detail = statusDetail(row)
  const missing = row.status === 'missing_required' || row.status === 'missing_optional'
  const draft = controller.draft(row.field)
  const editing = draft !== undefined
  const showEditor = controller.mayEdit && row.editable && (editing || missing)
  const error = controller.error(row.field)
  const text = valueText(row)
  const busy = controller.busy !== null

  return (
    <div
      data-field={row.field}
      data-status={row.status}
      className="border-b border-white/[0.04] px-3 py-1.5 hover:bg-white/[0.015]"
    >
      <div className={ROW_GRID}>
        <div className="min-w-0 pt-1">
          <div className="truncate text-[12px] text-stone-200" title={`${row.label} (${row.field})`}>
            {row.label}
            {row.required ? (
              <span className="ml-0.5 text-amber-300" aria-label="required" title="Required to screen">
                *
              </span>
            ) : null}
          </div>
        </div>

        <div className="min-w-0">
          {showEditor ? (
            <ValueEditor row={row} controller={controller} />
          ) : text ? (
            <div className="flex min-w-0 items-center pt-1 text-[12px] text-stone-100">
              <span className="truncate" title={text}>
                {text}
              </span>
              {row.unit && row.kind === 'number' && row.unit !== 'x' ? (
                <span className="ml-1 text-[10px] text-stone-500">{row.unit}</span>
              ) : null}
            </div>
          ) : (
            <div className="pt-1 text-[12px] text-stone-600">
              {row.status === 'not_applicable' ? 'N/A' : '—'}
            </div>
          )}
        </div>

        <SourceCell row={row} />

        <div className="min-w-0 pt-0.5">
          <span
            className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide ${meta.className}`}
            data-testid={`status-${row.field}`}
          >
            {meta.label}
          </span>
          {detail ? (
            <div className="mt-0.5 break-words text-[10px] leading-snug text-stone-500" title={detail}>
              {detail}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-1 pt-0.5">
          {controller.mayEdit ? (
            <>
              <button
                type="button"
                onClick={() => controller.confirm(row)}
                disabled={!row.confirmable || busy}
                aria-label={`Confirm ${row.label}`}
                title={row.confirmable ? 'Confirm this value as it stands' : (row.confirmBlocked ?? '')}
                className="inline-flex h-6 w-6 items-center justify-center rounded border border-emerald-400/25 text-emerald-300 hover:bg-emerald-400/10 disabled:cursor-not-allowed disabled:opacity-25"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => controller.setDraft(row.field, editing ? undefined : editDraftOf(row))}
                disabled={!row.editable || busy || missing}
                aria-label={`Edit ${row.label}`}
                title={row.editable ? (missing ? 'Type in the box' : 'Edit this value') : (row.editBlocked ?? '')}
                className="inline-flex h-6 w-6 items-center justify-center rounded border border-stone-600 text-stone-300 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-25"
              >
                <Pencil className="h-3 w-3" />
              </button>
              {controller.clearing === row.field ? (
                <button
                  type="button"
                  onClick={() => controller.clear(row)}
                  disabled={busy}
                  aria-label={`Confirm clearing ${row.label}`}
                  className="inline-flex h-6 items-center rounded border border-red-400/40 bg-red-400/10 px-1.5 text-[10px] text-red-200"
                >
                  Clear?
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => controller.askClear(row.field)}
                  disabled={!row.clearable || busy}
                  aria-label={`Clear ${row.label}`}
                  title={row.clearable ? 'Reject this value — nothing replaces it' : 'Nothing to clear here'}
                  className="inline-flex h-6 w-6 items-center justify-center rounded border border-stone-600 text-stone-400 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-25"
                >
                  <Eraser className="h-3 w-3" />
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {error ? (
        <p role="alert" className="mt-1 text-[11px] text-red-300">
          {error}
        </p>
      ) : null}
      <div className={ROW_GRID}>
        <ConflictPanel row={row} controller={controller} />
      </div>
    </div>
  )
}
